/**
 * Web adapter: write a {@link DerivedPalette} onto the document as the CSS
 * variables our app already reads. Call sites are untouched — they keep using
 * `rgb(var(--bg) / alpha)` etc.; we just feed those vars from the derived
 * palette instead of the four hand-tuned blocks in index.css.
 *
 * Sets two groups:
 *   1. Bunker bridge tokens (`--bg`, `--ink`, `--bk-accent`, `--surface-*`, …) —
 *      every custom Aether surface reads these.
 *   2. A few HeroUI core tokens (`--accent`, `--default`, `--surface-secondary`,
 *      `--border`, `--separator`, `--muted`) so HeroUI components adopt the
 *      chosen accent + neutral tint. HeroUI derives its own hover/soft tokens
 *      from these via color-mix, so they follow automatically.
 */
import type { DerivedPalette } from '@bunker/shared/theme';
import { hexToTriplet } from '@bunker/shared/theme';

/** Lerp a hex colour toward another by `t` (0..1). Used to tune the danger
 *  TEXT shade per theme mode so it stays legible on any surface. */
function mixHex(hex: string, toward: string, t: number): string {
  // Normalise to a 6-digit hex; bail on anything malformed so a bad input
  // can't write "#NaNNaNNaN" into a CSS variable (mirrors `lighten`'s guard).
  const norm = (h: string): string | null => {
    const x = h.replace('#', '');
    const f = x.length === 3 ? x.split('').map((c) => c + c).join('') : x;
    return /^[0-9a-fA-F]{6}$/.test(f) ? f : null;
  };
  const a = norm(hex);
  const b = norm(toward);
  if (!a || !b) return hex;
  const parse = (f: string): [number, number, number] =>
    [0, 2, 4].map((i) => parseInt(f.slice(i, i + 2), 16)) as [number, number, number];
  const [r1, g1, b1] = parse(a);
  const [r2, g2, b2] = parse(b);
  const m = (x: number, y: number) => Math.round(x + (y - x) * t);
  const to2 = (n: number) => n.toString(16).padStart(2, '0');
  return `#${to2(m(r1, r2))}${to2(m(g1, g2))}${to2(m(b1, b2))}`;
}

/** Apply a derived palette to <html> as inline CSS variables (wins over the
 *  static index.css blocks, which become the no-JS fallback). */
export function applyDerivedPaletteVars(p: DerivedPalette): void {
  if (typeof document === 'undefined') return;
  const s = document.documentElement.style;
  const set = (name: string, value: string) => s.setProperty(name, value);
  const tri = (name: string, hex: string) => set(name, hexToTriplet(hex));

  // ── Bunker bridge tokens (rgb-triplet form: "R G B") ──────────────────────
  tri('--bg', p.canvas);
  tri('--bg-subtle', p.outer);
  tri('--bg-raised', p.surface);
  tri('--bg-hover', p.hover);
  tri('--overlay-tint', p.overlayTint);

  tri('--ink', p.ink);
  tri('--ink-dim', p.inkMuted);
  tri('--ink-muted', p.inkMuted);

  tri('--bk-accent', p.accent);
  tri('--bk-accent-hover', p.accentHover);
  tri('--bk-accent-soft', p.accentText);
  tri('--bk-secondary', p.secondary);
  tri('--bk-tertiary', p.accent);
  tri('--bk-success', p.success);
  tri('--bk-success-dim', p.success);
  tri('--bk-danger', p.danger);
  // `-soft` is danger used as TEXT (kick / leave / error labels). A mid-red is
  // dim on a dark surface and washed on a light one, so lift it toward white on
  // dark themes and deepen it on light — `text-danger-ink` then reads on every
  // palette, not just the few where the raw danger happened to contrast.
  tri('--bk-danger-soft', mixHex(p.danger, p.isLight ? '#000000' : '#ffffff', p.isLight ? 0.3 : 0.4));
  tri('--bk-warning', p.warning);
  tri('--bk-warning-soft', p.warning);

  // Cispracs platform action (cispracs.ru): the sign-in button keeps the
  // platform's cyan whatever the Bunker accent is, in every theme.
  tri('--cp-action', '#00cbea');
  tri('--cp-action-hover', '#26e5f4');
  tri('--cp-action-sheen', '#9af6ff');
  tri('--cp-action-ink', '#061115');

  // Surface families — solid (alpha 1); the *-alpha companions stay at 1.
  tri('--surface-card', p.surfaceRaised);
  set('--surface-card-alpha', '1');
  tri('--surface-section', p.surfaceSecondary);
  set('--surface-section-alpha', '1');
  tri('--surface-input', p.surfaceSecondary);
  set('--surface-input-alpha', '1');
  tri('--surface-bubble-foreign', p.bubbleIn);
  set('--surface-bubble-foreign-alpha', '1');
  tri('--chrome-bg', p.surfaceSecondary);
  set('--chrome-bg-alpha', '1');

  // Rings/borders — a neutral border tint; light themes use the dark base tint
  // at low alpha, dark uses the lighter hover neutral opaque.
  const ringTri = p.isLight ? p.overlayTint : p.hover;
  tri('--ring-inset', ringTri);
  set('--ring-inset-alpha', p.isLight ? '0.18' : '1');
  tri('--ring-input', ringTri);
  set('--ring-input-alpha', p.isLight ? '0.18' : '1');

  // Row hover/press tint — base-aware (warm-white on dark, dark tint on light).
  tri('--row-hover', p.overlayTint);
  set('--row-hover-alpha', p.isLight ? '0.08' : '0.07');

  set(
    '--shadow-card',
    p.isLight ? '0 1px 2px 0 rgba(40, 30, 20, 0.08)' : '0 1px 0 0 rgba(255, 255, 255, 0.04)',
  );

  // ── HeroUI core tokens (accept any colour value) ──────────────────────────
  // Override the primaries; HeroUI's color-mix derivations (hover/soft/etc.)
  // follow. Crucially we override the NEUTRAL surface tokens too — otherwise
  // HeroUI-rendered surfaces (modals, popovers, cards, the settings dialog) keep
  // their native white/near-black and only our own Aether surfaces tint, so the
  // Base knob looked like it "only changed the selects".
  set('--background', p.canvas);
  set('--foreground', p.ink);
  set('--surface', p.surface);
  set('--surface-foreground', p.ink);
  set('--surface-secondary', p.surfaceSecondary);
  set('--surface-tertiary', p.hover);
  // Overlays (modals / popovers / menus / tooltips) — slightly elevated surface.
  set('--overlay', p.surfaceRaised);
  set('--overlay-foreground', p.ink);
  set('--accent', p.accent);
  set('--accent-foreground', '#ffffff');
  set('--focus', p.accent);
  set('--default', p.hover);
  set('--default-foreground', p.ink);
  set('--muted', p.inkMuted);
  // On light themes the raw hairline is barely there, so the shell panels (rail
  // / sidebar / chat / composer) melted into one sheet. Use a stronger ink-based
  // seam on light so every `border-separator` reads; dark keeps the subtle line.
  const seam = p.isLight ? `rgb(${hexToTriplet(p.ink)} / 0.16)` : p.hairline;
  set('--border', p.isLight ? `rgb(${hexToTriplet(p.ink)} / 0.2)` : p.hairline);
  set('--separator', seam);
}
